// Utility functions
const $ = selector => document.querySelector(selector);
const $$ = selector => document.querySelectorAll(selector);

// Constants
const SELECTORS = {
  pickerItem: '.picker-item',
  quantitySelector: '.quantity-selector',
  productCards: '.product-cards',
  galleryMobile: '.gallery-mobile', 
  addToCartButton: 'button[type="submit"]',
  subscriptionOptions: '.subscription-summary',
  productSummary: '.product-summary',
  productHeader: '.product__hdr',
  progressMessage: '.byo-progress__message',
  progressBars: '.byo-progress__bar',
  progressIcon: '.byo-progress__icon',
  progressContent: '.byo-progress__content',
  progressSubMessage: '.byo-progress__sub-message',
  byoProgress: '.byo-progress',
  subscriptionCheckbox: 'input[name="is_subscription"]',
  subscriptionSelect: '#selling_plan',
  sellingPlanSelect: '#selling-plan-select', // Real select element
  cupsPerDay: '[data-cups-day]',
  bundleTotal: '[data-bundle-total]',
  bundleCompareTotal: '[data-bundle-compare-total]',
  bagsTotal: '[data-bags-total]',
  subscribeTab: '#subscribe-tab',
  buyonceTab: '#buyonce-tab',
  addToCartForm: 'form[action="/cart/add"]'
};

const PROGRESS_MESSAGES = {
  0: "Add 3 bags for free shipping",
  1: "2 more bags for free shipping",
  2: "Just 1 more for free shipping...", 
  3: "Woop! Shipping is FREE!"
};

const BEANS_PER_CUP = 15; // grams
const SUBSCRIPTION_DISCOUNT = 5.00; // $5 off for subscriptions

// Product Manager
class ProductManager {
  constructor() {
    this.cache = new Map();
    this.init();
  }

  init() {
    this.preloadProducts();
    this.initQuantitySelectors();
    this.initProductSwitching();
    this.initSubscriptionToggle();
    this.updateCartButton();
    this.updateSubscriptionVisibility();
    this.updateProgressBars();

    // Add form submit handler
    this.form = $(SELECTORS.addToCartForm);
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }
  }

  initSubscriptionToggle() {
    const subscribeTab = $(SELECTORS.subscribeTab);
    const buyonceTab = $(SELECTORS.buyonceTab);

    if (subscribeTab && buyonceTab) {
      [subscribeTab, buyonceTab].forEach(tab => {
        tab.addEventListener('click', () => {
          this.updateBundleTotals();
        });
      });
    }
  }

  async preloadProducts() {
    const pickerItems = $$(SELECTORS.pickerItem);
    for (const item of pickerItems) {
      const url = item.dataset.url;
      if (!this.cache.has(url)) {
        try {
          const response = await fetch(url);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          this.cache.set(url, {
            cards: doc.querySelector(SELECTORS.productCards)?.innerHTML,
            gallery: doc.querySelector(SELECTORS.galleryMobile)?.innerHTML,
            header: doc.querySelector(SELECTORS.productHeader)?.innerHTML
          });
        } catch (error) {
          console.error('Error preloading product:', error);
        }
      }
    }
  }

  initQuantitySelectors() {
    $$(SELECTORS.pickerItem).forEach(item => {
      const quantitySelector = item.querySelector(SELECTORS.quantitySelector);
      if (!quantitySelector) return;

      const buttons = quantitySelector.querySelectorAll('button');
      const valueDisplay = quantitySelector.querySelector('.quantity-selector__value');
      const [decreaseBtn, increaseBtn] = buttons;

      increaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(valueDisplay.querySelector('span').textContent) || 0;
        const newValue = currentValue + 1;
        
        valueDisplay.querySelector('span').textContent = newValue;
        valueDisplay.classList.remove('hide');
        decreaseBtn.classList.remove('hide');
        item.classList.add('has-quantity');
        
        this.updateCartButton();
        this.updateSubscriptionVisibility();
        this.updateQuantityCalculations();
        this.updateProgressBars();
        this.updateBundleTotals();
      });

      decreaseBtn.addEventListener('click', () => {
        const currentValue = parseInt(valueDisplay.querySelector('span').textContent);
        const newValue = Math.max(0, currentValue - 1);
        
        valueDisplay.querySelector('span').textContent = newValue;
        
        if (newValue === 0) {
          valueDisplay.classList.add('hide');
          decreaseBtn.classList.add('hide');
          item.classList.remove('has-quantity');
        }
        
        this.updateCartButton();
        this.updateSubscriptionVisibility();
        this.updateQuantityCalculations();
        this.updateProgressBars();
        this.updateBundleTotals();
      });
    });
  }

  initProductSwitching() {
    $$(SELECTORS.pickerItem).forEach(item => {
      item.addEventListener('click', (e) => {
        // Don't switch if clicking quantity buttons
        if (e.target.closest(SELECTORS.quantitySelector)) return;
        
        const url = item.dataset.url;
        const cachedContent = this.cache.get(url);
        
        if (cachedContent) {
          $(SELECTORS.productCards).innerHTML = cachedContent.cards;
          $(SELECTORS.galleryMobile).innerHTML = cachedContent.gallery;
          $(SELECTORS.productHeader).innerHTML = cachedContent.header;
          
          // Update URL and active states
          history.pushState(null, '', url);
          $$(SELECTORS.pickerItem).forEach(p => {
            p.classList.remove('active');
          });
          item.classList.add('active');
        }
      });
    });
  }

  updateCartButton() {
    const button = $(SELECTORS.addToCartButton);
    if (!button) return;

    const totalQuantity = this.getTotalQuantity();
    const totalPrice = this.getTotalPrice();

    if (totalQuantity > 0) {
      button.querySelector('span').textContent = 'Add to Cart';
      button.disabled = false;
    } else {
      button.querySelector('span').textContent = 'No bags added yet';
      button.disabled = true;
    }
  }

  updateSubscriptionVisibility() {
    const totalQuantity = this.getTotalQuantity();
    const subscriptionOptions = $(SELECTORS.subscriptionOptions);
    const productSummary = $(SELECTORS.productSummary);
    
    if (subscriptionOptions) {
      subscriptionOptions.style.display = totalQuantity > 0 ? 'block' : 'none';
    }
    
    if (productSummary) {
      productSummary.style.display = totalQuantity > 0 ? 'flex' : 'none';
    }
  }

  updateBundleTotals() {
    const totalPrice = this.getTotalPrice();
    const comparePrice = this.getTotalComparePrice();
    const totalQuantity = this.getTotalQuantity();
    const isSubscriptionActive = this.isSubscriptionTabActive();
    
    const bundleTotal = $(SELECTORS.bundleTotal);
    const bundleCompareTotal = $(SELECTORS.bundleCompareTotal);
    const bagsTotal = $(SELECTORS.bagsTotal);
    
    if (bundleTotal) {
      if (isSubscriptionActive) {
        const discountedPrice = (parseFloat(totalPrice) - SUBSCRIPTION_DISCOUNT).toFixed(2);
        bundleTotal.textContent = `$${discountedPrice.endsWith('.00') ? discountedPrice.slice(0, -3) : discountedPrice}`;
      } else {
        bundleTotal.textContent = `$${totalPrice.endsWith('.00') ? totalPrice.slice(0, -3) : totalPrice}`;
      }
    }
    
    if (bundleCompareTotal) {
      if (isSubscriptionActive) {
        bundleCompareTotal.textContent = `$${totalPrice.endsWith('.00') ? totalPrice.slice(0, -3) : totalPrice}`;
        bundleCompareTotal.style.display = 'inline';
      } else {
        bundleCompareTotal.style.display = 'none';
      }
    }

    if (bagsTotal) {
      bagsTotal.textContent = `${totalQuantity} ${totalQuantity === 1 ? 'bag' : 'bags'}`;
    }
  }

  updateProgressBars() {
    const totalQuantity = Math.min(this.getTotalQuantity(), 3);
    const messageEl = $(SELECTORS.progressMessage);
    const progressContent = $(SELECTORS.progressContent);
    const bars = $$(SELECTORS.progressBars);
    const icon = $(SELECTORS.progressIcon);
    const subMessage = $(SELECTORS.progressSubMessage);
    const byoProgress = $(SELECTORS.byoProgress);

    // Update message and content active state
    if (messageEl && progressContent) {
      messageEl.textContent = PROGRESS_MESSAGES[Math.min(totalQuantity, 3)];
      if (totalQuantity > 0) {
        progressContent.classList.add('is-active');
        byoProgress.classList.add('is-active');
      } else {
        progressContent.classList.remove('is-active');
        byoProgress.classList.remove('is-active');
      }
    }

    // Update progress bars
    bars.forEach((bar, index) => {
      if (index < totalQuantity) {
        bar.classList.add('is-active');
      } else {
        bar.classList.remove('is-active');
      }
    });

    // Update sub message calculations
    if (subMessage) {
      const totalWeight = this.getTotalWeight();
      const totalCups = Math.floor(totalWeight / BEANS_PER_CUP);
      const totalPrice = parseFloat(this.getTotalPrice());
      const pricePerCup = totalCups > 0 ? Math.floor(totalPrice / totalCups * 100) / 100 : 0;

      const quantitySpan = subMessage.querySelector('[data-total-quantity-selected]');
      const cupsSpan = subMessage.querySelector('[data-total-quantity-cups]');
      const priceSpan = subMessage.querySelector('[data-total-quantity-price]');

      if (quantitySpan) quantitySpan.textContent = this.getTotalQuantity();
      if (cupsSpan) cupsSpan.textContent = totalCups;
      if (priceSpan) priceSpan.textContent = `$${pricePerCup.toFixed(2)}`;
    }

    // Handle completion
    if (totalQuantity === 3) {
      icon.classList.add('is-complete');
      if (!icon.dataset.confettiTriggered) {
        this.triggerConfetti(icon);
        icon.dataset.confettiTriggered = true;
      }
    } else {
      icon.classList.remove('is-complete');
      icon.dataset.confettiTriggered = '';
    }
  }

  triggerConfetti(icon) {
    const rect = icon.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const colors = ['#FF5C00', '#FFB800', '#FF8A00'];
    const particleCount = 50;
    const particles = [];

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      const size = 3 + Math.random() * 4;
      particle.style.cssText = `
        position: fixed;
        width: ${size}px;
        height: ${size}px;
        background: ${colors[Math.floor(Math.random() * colors.length)]};
        left: ${centerX}px;
        top: ${centerY}px;
        pointer-events: none;
        border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
        transform: translate(-50%, -50%) scale(0);
      `;
      document.body.appendChild(particle);

      // Calculate initial velocity with 60% reduction
      const angle = (Math.random() * Math.PI * 2);
      const velocity = (8 + Math.random() * 6) * 0.4; // Reduced by 60%
      const vx = Math.cos(angle) * velocity;
      const vy = Math.sin(angle) * velocity;

      particles.push({
        element: particle,
        x: centerX,
        y: centerY,
        vx,
        vy,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 15
      });
    }

    let start = performance.now();
    
    // Animate particles
    function animate(currentTime) {
      const elapsed = (currentTime - start) / 1000;
      let allDone = true;

      particles.forEach(particle => {
        // Apply gravity and friction
        particle.vy += 9.81 * elapsed * 0.5; // Reduced gravity by 50%
        particle.vx *= 0.99; // air resistance
        
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        // Update rotation
        particle.rotation += particle.rotationSpeed;

        // Calculate opacity based on lifetime
        const opacity = Math.max(0, 1 - (elapsed * 0.7));
        
        if (opacity > 0) {
          allDone = false;
          particle.element.style.transform = `
            translate(${particle.x - centerX}px, ${particle.y - centerY}px)
            rotate(${particle.rotation}deg)
            scale(${Math.min(1, elapsed * 4)})
          `;
          particle.element.style.opacity = opacity;
        } else {
          particle.element.remove();
        }
      });

      if (!allDone) {
        requestAnimationFrame(animate);
      }
    }

    requestAnimationFrame(animate);
  }

  updateQuantityCalculations() {
    const totalWeight = this.getTotalWeight();
    const totalCups = Math.floor(totalWeight / BEANS_PER_CUP);
    
    // Update cups per day for each duration option
    $$(SELECTORS.cupsPerDay).forEach(el => {
      const days = parseInt(el.getAttribute('data-cups-day'));
      if (!isNaN(days)) {
        const cupsPerDay = Math.round(totalCups / days * 5) / 5; // Round to nearest 0.2
        el.textContent = cupsPerDay;
      }
    });
  }

  /**
   * Check if the subscription tab is currently active
   * @returns {boolean} True if subscription tab is active
   */
  isSubscriptionTabActive() {
    const subscribeTab = $(SELECTORS.subscribeTab);
    return subscribeTab?.getAttribute('aria-selected') === 'true';
  }

  /**
   * Get the selected selling plan value from the custom dropdown
   * @returns {string|null} The selling plan value or null if none selected
   */
  getSelectedSellingPlan() {
    const sellingPlanSelect = $(SELECTORS.sellingPlanSelect);
    return sellingPlanSelect?.value || null;
  }

  getTotalQuantity() {
    return Array.from($$(SELECTORS.pickerItem)).reduce((total, item) => {
      const quantitySpan = item.querySelector('.quantity-selector__value span');
      return total + (parseInt(quantitySpan?.textContent) || 0);
    }, 0);
  }

  getTotalPrice() {
    return Array.from($$(SELECTORS.pickerItem)).reduce((total, item) => {
      const quantity = parseInt(item.querySelector('.quantity-selector__value span')?.textContent) || 0;
      const price = parseFloat(item.dataset.productPrice) / 100;
      return total + (quantity * price);
    }, 0).toFixed(2);
  }

  getTotalComparePrice() {
    return Array.from($$(SELECTORS.pickerItem)).reduce((total, item) => {
      const quantity = parseInt(item.querySelector('.quantity-selector__value span')?.textContent) || 0;
      const comparePrice = parseFloat(item.dataset.productComparePrice) / 100 || 0;
      return total + (quantity * comparePrice);
    }, 0).toFixed(2);
  }

  getTotalWeight() {
    return Array.from($$(SELECTORS.pickerItem)).reduce((total, item) => {
      const quantity = parseInt(item.querySelector('.quantity-selector__value span')?.textContent) || 0;
      const weight = parseFloat(item.dataset.productWeight);
      return total + (quantity * weight);
    }, 0);
  }

  async addToCart() {
    // Log initial state
    console.log('Starting addToCart...');
    
    const items = Array.from($$(SELECTORS.pickerItem))
      .filter(item => parseInt(item.querySelector('.quantity-selector__value span')?.textContent) > 0)
      .map(item => ({
        id: parseInt(item.dataset.productId), // Already using variant ID from HTML
        quantity: parseInt(item.querySelector('.quantity-selector__value span').textContent)
      }));

    console.log('Items to add:', items);

    // Check if subscription tab is active and get selling plan
    const isSubscriptionActive = this.isSubscriptionTabActive();
    console.log('Is subscription active:', isSubscriptionActive);

    if (isSubscriptionActive) {
      const selectedSellingPlan = this.getSelectedSellingPlan();
      console.log('Selected selling plan:', selectedSellingPlan);

      // Add selling plan to all items if subscription is active and a plan is selected
      if (selectedSellingPlan) {
        items.forEach(item => item.selling_plan = selectedSellingPlan);
        console.log('Added selling plan to items:', items);
      } else {
        console.warn('Subscription is active but no selling plan selected');
      }
    } else {
      console.log('Buy once selected - no selling plan will be added');
    }

    const requestBody = { items };
    console.log('Final request body:', requestBody);

    try {
      console.log('Sending request to /cart/add.js...');
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        console.error('Server returned error:', responseData);
        throw new Error('Add to cart failed');
      }

      // Update rewards if available
      try {
        console.log('Updating rewards...');
        await window.rewardAutomation?.updateRewardStatus();
        console.log('Rewards updated successfully');
      } catch (error) {
        console.error('Error updating rewards:', error);
      }

      // Use the global miniCart instance to open the cart drawer
      console.log('Opening cart...');
      if (window.miniCart) {
        window.miniCart.openDrawer();
        await window.miniCart.refreshCart();
      } else {
        console.warn('miniCart not found, falling back to window.openCart');
        if (typeof window.openCart === 'function') {
          window.openCart();
        }
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      console.error('Stack trace:', error.stack);
    }
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  window.productManager = new ProductManager();
});